import style from "./AboutCourseSection.module.css";
import { secondsToHoursMinutes } from "@/utils/helpers";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "@/ui/icon/Icon";
import EuFunding from "@/ui/euFunding/EuFunding";
import { useLocalized } from "@/i18n/useLocalized";

function AboutCourseSection({ unit }) {
	const { t } = useTranslation();
	const [isExpanded, setIsExpanded] = useState(false);
	const l = useLocalized();
	const description = l(unit?.course, "description") || "";

	const truncatedText = description.slice(0, 800);
	const lastEnterIndex = truncatedText.lastIndexOf("\n");
	const truncateIndex =
		lastEnterIndex > 0 ? lastEnterIndex : truncatedText.lastIndexOf(" ");
	const truncatedDescription = description.slice(0, truncateIndex + 1);
	const shouldShowButton = description.length > truncatedDescription.length;

	return (
		<div className={style.grid}>
			{/* One grid child, not two: the description carries the show-more
			    gradient positioned against itself, and the grid collapses to a
			    two-item column lower down. */}
			<div className={style.primary}>
				{unit?.course?.is_free && (
					<EuFunding statement className={style.euFunding} />
				)}

				<div className={style.description}>
					<div
						dangerouslySetInnerHTML={{
							__html: isExpanded
								? description
								: truncatedDescription,
						}}
					/>
					{description.length > 800 && shouldShowButton && (
						<button
							className={`${style.descriptionShowMore} ${
								isExpanded ? style.expanded : ""
							}`}
							onClick={() => setIsExpanded(!isExpanded)}
						>
							{isExpanded
								? t("courses:landing.description.showLess")
								: t("courses:landing.description.showMore")}
						</button>
					)}
				</div>
			</div>
			<div className={style.other}>
				<Lecturers lecturers={unit.course.lecturers} />
				<CourseStats course={unit.course} />
			</div>
		</div>
	);
}

function Lecturers({ lecturers }) {
	return (
		<>
			{lecturers.map((lecturer, index) => (
				<LecturerCard key={index} lecturer={lecturer} />
			))}
		</>
	);
}

function LecturerCard({ lecturer }) {
	const { t } = useTranslation();
	const [isExpanded, setIsExpanded] = useState(false);
	const l = useLocalized();
	const maxLength = 190; // characters to show initially

	const truncatedBio = l(lecturer, "bio")?.slice(0, maxLength);
	const shouldShowButton = l(lecturer, "bio")?.length > maxLength;

	return (
		<div className={style.lecturer}>
			<div className={style.header}>
				<div className={style.imgHolder}>
					<img src={lecturer?.picture_url} alt={t("common:avatar.profile")} />
				</div>
				<div className={style.info}>
					<div className={style.nameHolder}>
						<div className={style.fullName}>{lecturer?.name}</div>
						<div className={style.icoHolder}>
							<Icon iconType="fa-solid" icon="fa-badge-check" />
						</div>
					</div>
					<div className={style.jobTitle}>
						{l(lecturer, "job_title")}
					</div>
				</div>
			</div>
			<div className={style.body}>
				<span className={style.bio}>
					{isExpanded ? l(lecturer, "bio") : truncatedBio}
					{!isExpanded && shouldShowButton && "..."}
				</span>
				{shouldShowButton && (
					<button
						className={style.showMoreBtn}
						onClick={() => setIsExpanded(!isExpanded)}
					>
						{isExpanded
							? t("player:about.seeLess")
							: t("player:about.seeMore")}
					</button>
				)}
			</div>
		</div>
	);
}

function CourseStats({ course }) {
	const { t } = useTranslation();
	const l = useLocalized();
	const difficulty = ["easy", "medium", "hard"].includes(course?.difficulty)
		? course.difficulty
		: "easy";

	const stats = [
		{
			label: t("courses:difficulty.label"),
			value: t(`courses:difficulty.${difficulty}`),
			icon: "fa-bullseye",
		},
		{
			label: t("courses:landing.sidebar.duration"),
			value:
				(course.duration && secondsToHoursMinutes(course?.duration)) ||
				0,
			icon: "fa-clock",
		},
		{
			label: t("player:about.modules"),
			value: course?.module_count || 0,
			icon: "fa-layer-group",
		},
	];

	return (
		<div className={style.courseStats}>
			<div className={style.statsGrid}>
				{stats.map((stat, index) => (
					<div key={index} className={style.statItem}>
						<div className={style.icoHolder}>
							<Icon icon={stat.icon} />
						</div>
						<div className={style.statInfo}>
							<span className={style.statLabel}>
								{stat.label}
							</span>
							<span className={style.statValue}>
								{stat.value}
							</span>
						</div>
					</div>
				))}
			</div>

			<div className={style.categorySection}>
				<div className={style.category}>
					{course?.categories.map((category) => (
						<span
							// style={makeTagColors(category.name)}
							key={category.slug}
						>
							{l(category, "name")}
						</span>
					))}
				</div>
			</div>
		</div>
	);
}

export default AboutCourseSection;
